import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "@/components/layout/Sidebar";
import { titleForPath } from "@/components/layout/proRouteTitles";
import NotificationCenter from "@/components/notifications/NotificationCenter";
import { useAuth } from "@/contexts/AuthContext";
import { User, Settings, LogOut, ChevronDown, Menu, BellRing, BellOff, Send, AlertCircle, Scale } from "lucide-react";
import { subscribeToPush, isPushSubscribed, getBackendPushStatus, unsubscribeFromPush, sendTestPush } from "@/utils/pushNotifications";
import { toast } from "sonner";

export default function ProfessionalLayout({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const headerTitle = title || titleForPath(pathname);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // Push state: unknown | active | inactive | mismatch | unsupported
  //   active   = browser + backend both subscribed
  //   inactive = neither
  //   mismatch = browser has sub but backend doesn't (or inverse) — needs reactivation
  const [pushState, setPushState] = useState("unknown");
  const [pushLoading, setPushLoading] = useState(false);
  const menuRef = useRef(null);
  const showNotifications = ["IDE", "MEDECIN", "SECRETAIRE_MEDICAL"].includes(user?.role);
  const canHavePush = ["IDE", "MEDECIN", "SECRETAIRE_MEDICAL", "ADMIN_CLINIQUE"].includes(user?.role);
  const pushRoleCopy = user?.role === "MEDECIN"
    ? "Recevoir escalades IDE et chimios à valider."
    : user?.role === "IDE"
      ? "Recevoir check-ins patients et alertes."
      : "Activez les notifications pour être alerté instantanément.";

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cross-check browser + backend
  const refreshPushState = async () => {
    if (!canHavePush) return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setPushState("unsupported");
      return;
    }
    const [browserSub, backend] = await Promise.all([
      isPushSubscribed(),
      getBackendPushStatus(),
    ]);
    if (browserSub && backend.subscribed) setPushState("active");
    else if (!browserSub && !backend.subscribed) setPushState("inactive");
    else setPushState("mismatch");
  };

  useEffect(() => {
    refreshPushState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleEnablePush = async () => {
    setPushLoading(true);
    try {
      const ok = await subscribeToPush();
      if (ok) {
        toast.success("Notifications push activées");
        await refreshPushState();
      } else {
        toast.error("Activation refusée. Vérifiez les permissions du navigateur.");
      }
    } catch {
      toast.error("Erreur lors de l'activation");
    } finally {
      setPushLoading(false);
    }
  };

  const handleReactivate = async () => {
    setPushLoading(true);
    try {
      // Clean everything first, then re-subscribe cleanly
      await unsubscribeFromPush();
      const ok = await subscribeToPush();
      if (ok) {
        toast.success("Notifications resynchronisées");
      } else {
        toast.error("Impossible de réactiver. Réessayez ou vérifiez les permissions.");
      }
      await refreshPushState();
    } finally {
      setPushLoading(false);
    }
  };

  const handleDisablePush = async () => {
    setPushLoading(true);
    try {
      const ok = await unsubscribeFromPush();
      if (ok) {
        toast.success("Notifications désactivées");
      } else {
        toast.error("Erreur lors de la désactivation");
      }
      await refreshPushState();
    } finally {
      setPushLoading(false);
    }
  };

  const handleTestPush = async () => {
    setPushLoading(true);
    try {
      const r = await sendTestPush();
      if (r.status === "ok" && r.sent > 0) {
        toast.success("Notification test envoyée ! Vérifiez votre écran.");
      } else if (r.status === "no_subscription") {
        toast.error(r.message || "Activez d'abord les notifications");
        await refreshPushState();
      } else {
        toast.error("Échec du test. Ré-activez les notifications.");
        await refreshPushState();
      }
    } finally {
      setPushLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50/80 flex">
      <Sidebar
        isMobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />
      <main className="flex-1 lg:ml-64 transition-all duration-300 min-w-0">
        <div className="min-h-screen">
          {/* Header with notifications and user menu */}
          <header className="bg-white border-b border-slate-200/80 sticky top-0 z-30 h-14 px-4 lg:px-8 flex items-center justify-between gap-3" data-testid="pro-header">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {/* Hamburger - mobile only */}
              <button
                onClick={() => setMobileSidebarOpen(true)}
                data-testid="mobile-menu-btn"
                aria-label="Menu"
                className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100 text-slate-600 min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <Menu className="w-6 h-6" />
              </button>
              {headerTitle ? (
                <h1 className="text-base lg:text-lg font-bold text-slate-900 font-display tracking-tight truncate" data-testid="header-title">{headerTitle}</h1>
              ) : null}
            </div>
            <div className="flex items-center gap-2 lg:gap-4 flex-shrink-0">
              {showNotifications && <NotificationCenter />}

              {/* User Menu */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  data-testid="user-menu-btn"
                  className="flex items-center gap-2 px-2 lg:px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors min-h-[40px] lg:border-l lg:border-slate-200 lg:rounded-l-none lg:pl-4"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white text-sm font-medium shadow-sm">
                    {user?.firstName?.[0]}{user?.lastName?.[0] || user?.email?.[0]?.toUpperCase()}
                  </div>
                  <span className="text-sm font-medium text-slate-700 hidden lg:block">
                    {user?.firstName || user?.email?.split('@')[0]}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 hidden lg:block ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-72 bg-white/95 backdrop-blur-xl rounded-xl shadow-lg border border-slate-200/60 py-2 z-50 animate-scale-in">
                    <div className="px-4 py-2.5 border-b border-slate-100">
                      <p className="text-sm font-semibold text-slate-800">{user?.firstName} {user?.lastName}</p>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">{user?.email}</p>
                      <span className="inline-block mt-1.5 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full border border-emerald-200/40">
                        {user?.role}
                      </span>
                    </div>

                    {/* Push notifications panel */}
                    {canHavePush && (
                      <div className="px-4 py-3 border-b border-slate-100 space-y-2">
                        {pushState === "active" && (
                          <>
                            <div className="flex items-center gap-2 text-xs text-emerald-700" data-testid="push-status-on">
                              <BellRing className="w-4 h-4" />
                              Notifications push actives
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={handleTestPush}
                                disabled={pushLoading}
                                data-testid="test-push-btn"
                                className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-semibold hover:bg-emerald-100 disabled:opacity-50 min-h-[36px]"
                              >
                                <Send className="w-3.5 h-3.5" />
                                Envoyer un test
                              </button>
                              <button
                                onClick={handleDisablePush}
                                disabled={pushLoading}
                                data-testid="disable-push-btn"
                                className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 bg-slate-50 text-slate-600 rounded-lg text-xs font-semibold hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50 min-h-[36px]"
                              >
                                <BellOff className="w-3.5 h-3.5" />
                                Désactiver
                              </button>
                            </div>
                          </>
                        )}
                        {pushState === "inactive" && (
                          <button
                            onClick={handleEnablePush}
                            disabled={pushLoading}
                            data-testid="enable-push-menu-btn"
                            className="w-full flex items-center gap-2 px-2 py-2 text-xs text-slate-700 hover:text-emerald-600 font-medium min-h-[36px]"
                          >
                            <BellRing className="w-4 h-4" />
                            {pushLoading ? "Activation..." : "Activer les notifications push"}
                          </button>
                        )}
                        {pushState === "mismatch" && (
                          <button
                            onClick={handleReactivate}
                            disabled={pushLoading}
                            data-testid="reactivate-push-menu-btn"
                            className="w-full flex items-center gap-2 px-2 py-2 text-xs text-amber-700 hover:text-amber-800 font-medium min-h-[36px]"
                          >
                            <AlertCircle className="w-4 h-4" />
                            {pushLoading ? "Synchronisation..." : "Resynchroniser les notifications"}
                          </button>
                        )}
                        {pushState === "unsupported" && (
                          <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="push-status-unsupported">
                            <BellOff className="w-4 h-4" />
                            Non supporté sur ce navigateur
                          </div>
                        )}
                        {pushState === "unknown" && (
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <BellRing className="w-4 h-4 animate-pulse" />
                            Vérification...
                          </div>
                        )}
                      </div>
                    )}

                    <button
                      onClick={() => { setShowUserMenu(false); navigate("/profile"); }}
                      data-testid="profile-link"
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 min-h-[44px]"
                    >
                      <User className="w-4 h-4" />
                      Mon profil
                    </button>
                    <button
                      onClick={() => { setShowUserMenu(false); navigate("/profile"); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 min-h-[44px]"
                    >
                      <Settings className="w-4 h-4" />
                      Changer mot de passe
                    </button>
                    <button
                      onClick={() => { setShowUserMenu(false); navigate("/legal"); }}
                      data-testid="legal-docs-menu-link"
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 min-h-[44px]"
                    >
                      <Scale className="w-4 h-4" />
                      Documents juridiques
                    </button>
                    <div className="border-t border-slate-100 mt-2 pt-2">
                      <button
                        onClick={handleLogout}
                        data-testid="logout-btn-header"
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 min-h-[44px]"
                      >
                        <LogOut className="w-4 h-4" />
                        Déconnexion
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Push subscription banner — compact, sous l'en-tête */}
          {canHavePush && (pushState === "inactive" || pushState === "mismatch") && (
            <div
              data-testid="push-banner"
              className={`h-9 px-4 lg:px-8 flex items-center justify-between gap-3 text-xs border-b ${
                pushState === "mismatch" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {pushState === "mismatch"
                  ? <AlertCircle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                  : <BellRing className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />}
                <span className="truncate font-medium">
                  {pushState === "mismatch" ? "Notifications désynchronisées — réactivez-les pour rester alerté." : pushRoleCopy}
                </span>
              </div>
              <button
                onClick={pushState === "mismatch" ? handleReactivate : handleEnablePush}
                disabled={pushLoading}
                data-testid={pushState === "mismatch" ? "reactivate-push-banner-btn" : "enable-push-banner-btn"}
                className={`shrink-0 px-3 py-1 rounded-md text-xs font-semibold text-white disabled:opacity-50 transition-colors ${
                  pushState === "mismatch" ? "bg-amber-600 hover:bg-amber-700" : "bg-primary hover:bg-primary/90"
                }`}
              >
                {pushLoading ? "..." : pushState === "mismatch" ? "Réactiver" : "Activer"}
              </button>
            </div>
          )}

          <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="pro-content">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}
